import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import YhInput from "../../../../../../ui/YhInput";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../../../helpers/methods";
import { getAllDepartmentWithDesignation } from "./TeamTabContentService";
import customErrorHandler from "../../../../../../utils/customErrorHandler";
import notifiers from "../../../../../../global/constants/NotificationConstants";
import urls from "../../../../../../global/constants/UrlConstants";

interface CustomProps {
  isAddTeamPage?: boolean;
  isDirectAddTeam?: boolean;
}

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const TeamTabContent = (_props: CustomProps) => {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const response1 = await getAllDepartmentWithDesignation();
      customErrorHandler(response1);
      const data2 = (response1.data || []).map((item: any) => ({
        id: item.department_Id || item._id,
        department_name: item.department_name,
        teams: item.teams || [],
        designation: item.designation || [],
        department_Id: item.department_Id || item._id,
      }));
      setRows(data2);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = rows.filter((row) => {
    if (!search) return true;
    return String(row.department_name || "")
      .toLowerCase()
      .includes(search.toLowerCase());
  });

  const pagedRows = filteredRows.slice(
    paginationModel.page * paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
  );

  const columns: YhTableColumn[] = [
    {
      headerName: "Department",
      field: "department_name",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-semibold capitalize text-[#111827]">
          {params.row?.department_name || "—"}
        </span>
      ),
    },
    {
      headerName: "Team",
      field: "teams",
      minWidth: 220,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex flex-wrap gap-1.5 py-1">
          {(params.row?.teams || []).length ? (
            params.row.teams.map((e: any, i: number) => (
              <span
                key={i}
                className="inline-flex rounded-lg bg-[#F3F4F6] px-2 py-0.5 text-xs text-[#374151]"
              >
                {e.team_name}
              </span>
            ))
          ) : (
            <span className="text-sm text-[#9CA3AF]">—</span>
          )}
        </div>
      ),
    },
    {
      headerName: "Designation",
      field: "designation",
      minWidth: 240,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex flex-wrap gap-1.5 py-1">
          {(params.row?.designation || []).length ? (
            params.row.designation.map((e: any, i: number) => (
              <span
                key={i}
                className="inline-flex rounded-lg bg-[#EEF0FF] px-2 py-0.5 text-xs text-[#4338CA]"
              >
                {e.designation_name}
              </span>
            ))
          ) : (
            <span className="text-sm text-[#9CA3AF]">—</span>
          )}
        </div>
      ),
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 100,
      renderCell: () => (
        <button
          type="button"
          className="text-sm font-semibold text-[#A02448] hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            history.push(urls.ADDDEPARTMENTPAGE);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="min-w-[240px] flex-1 sm:max-w-[420px]">
          <YhInput
            placeholder="Search departments…"
            value={search}
            type="search"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            startAdornment={<SearchSvg />}
            containerClassName="w-full max-w-[420px]"
          />
        </div>
        <YhButton
          variant="primary"
          size="sm"
          onClick={() => history.push(urls.ADDDEPARTMENTPAGE)}
        >
          + Create Department
        </YhButton>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={pagedRows}
          getRowId={(row) => row.id || row.department_Id}
          loading={loading}
          emptyMessage="No departments found"
          className="h-full"
          rowHeight={64}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={filteredRows.length}
          pageSizeOptions={[10, 25, 50]}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
        />
      </div>
    </div>
  );
};

export default TeamTabContent;
